import {Box, Button} from "@mui/material";
import React, {ReactElement} from "react";
import {Sidebar, SidebarItem, SidebarItems, SidebarListener, SidebarTab} from "./Sidebar";
import {PathComponent, PathProps} from "./index";
import {BaseApp} from "./BaseApp";
import {
  DIVIDER,
  PD_MD,
  PD_SM,
  PD_XXLG,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_MD,
  SIDEBAR_WIDTH_SM,
  SZ_ACTION_PANEL
} from "./dimens";
import {Action, MenuOption} from "./types";
import {parseHandle} from "./route_util";
import {KeyboardDoubleArrowLeftOutlined, KeyboardDoubleArrowRightOutlined} from "@mui/icons-material";
import {Outlet} from "react-router-dom";
import {NotFoundFragment} from "./NotFoundFragment";
import {gray} from "./colors";

export interface PageWithSidebarContainerRenderer {
  renderPageWithSidebarContainerSidebar(): ReactElement;

  renderPageWithSidebarContainerContent(): ReactElement;
}

const SIDEBAR_SIZES = new Map<string, number>([
  ["md", SIDEBAR_WIDTH_MD],
  ["sm", SIDEBAR_WIDTH_SM],
]);

export enum SidebarLocation {
  LEFT = "left",
  RIGHT = "right",
}

export type PageWithSidebarContainerProps = {
  size?: "md" | "sm",
  title?: string,
  style?: any,
  path?: PathProps,
  sidebarCollapsible?: boolean,
  sidebarDefaultCollapsed?: boolean,
  sidebarHidden?: boolean,
  sidebarLocation?: SidebarLocation,
  sidebarTabs?: SidebarTab[],
  sidebarItems?: SidebarItems,
  sidebarAction?: Action,
  sidebarStyleFlags?: number,
  sidebarBackground?: string,
  menuOptions?: MenuOption[],
  onMenuButtonClicked?: (menuOption: MenuOption) => boolean,
  onSidebarCollapsed?: (collapsed: boolean) => void,
  renderer?: PageWithSidebarContainerRenderer,
  showBackButton?: boolean,
}

export type PageWithSidebarContainerState = {
  selectedSidebarItem?: SidebarItem,
  sidebarCollapsed?: boolean,
}

export class PageWithSidebarContainer extends React.Component<PageWithSidebarContainerProps, PageWithSidebarContainerState>
  implements SidebarListener {

  private readonly theme = BaseApp.CONTEXT.getAppConfig().theme;

  constructor(props: PageWithSidebarContainerProps, context: any) {
    super(props, context);
    this.state = {
      sidebarCollapsed: props.sidebarDefaultCollapsed,
    };
  }

  static pathComponentFromSidebarItem(item: SidebarItem, containerId: string): PathComponent {
    if (!item.path) {
      return null;
    }
    return {
      path: item.path,
      handle: {containerId: containerId, path: item.path},
      children: item.nestedPaths,
      // render: (pathProps: PathProps) => <Outlet/>,//item.render?.(pathProps) || <PlaceholderFragment/>,
      render: (pathProps: PathProps) => item.nestedPaths
        ? <Outlet/>
        : (item.render?.(pathProps) || <NotFoundFragment/>),
    };
  }

  static disableSidebarSelectionFromPath(): boolean {
    return false;
  }

  static nestedPathsFromSidebarItems(items: SidebarItems): PathComponent[] {
    return this.nestedPathsFromSidebarItemsArray(items.groups.flatMap(group => group.items), items.containerId);
  }

  static nestedPathsFromSidebarItemsArray(items: SidebarItem[], containerId?: string): PathComponent[] {
    return items.reduce<PathComponent[]>((result: PathComponent[], item: SidebarItem) => {
      const pathComponent = this.pathComponentFromSidebarItem(item, containerId);
      if (pathComponent) {
        result.push(pathComponent);
      }
      return result;
    }, []);
  }

  static getDerivedStateFromProps(nextProps: Readonly<PageWithSidebarContainerProps>, prevState: Readonly<PageWithSidebarContainerState>) {
    let sidebarItem;
    if (nextProps.sidebarItems) {
      sidebarItem = PageWithSidebarContainer.selectedSidebarItemFromPath(nextProps.sidebarItems, nextProps.path);
    } else if (nextProps.sidebarTabs) {
      const selectedTab = nextProps.sidebarTabs.find(tab => {
        const sidebarTabItem = PageWithSidebarContainer.selectedSidebarItemFromPath(tab.items, nextProps.path, true);
        if (sidebarTabItem) {
          tab.selected = sidebarTabItem;
        }
        return sidebarTabItem;
      });
      if (selectedTab) {
        sidebarItem = selectedTab.selected;
      } else {
        sidebarItem = PageWithSidebarContainer.selectedSidebarItemFromPath(nextProps.sidebarTabs[0].items, nextProps.path);
      }
    }
    if (sidebarItem && (!prevState.selectedSidebarItem || sidebarItem.path) && prevState.selectedSidebarItem !== sidebarItem) {
      return {
        selectedSidebarItem: sidebarItem,
      };
    }
  }

  static selectedSidebarItemFromPath(items: SidebarItems, pathProps: PathProps, noDefault?: boolean): SidebarItem {
    const handle = pathProps?.matches.find(match => match.handle?.containerId === items.containerId)?.handle;
    const parsedHandlePath = parseHandle(pathProps, handle)?.path;
    let item = items.groups.flatMap(group => group.items).find(value => value.path === parsedHandlePath || value.path === handle?.path);
    if (!item && !noDefault) {
      item = items.groups[0]?.items[0];
    }
    return item;
  }

  componentDidUpdate(prevProps: Readonly<PageWithSidebarContainerProps>, prevState: Readonly<PageWithSidebarContainerState>, snapshot?: any) {
    if (prevState.sidebarCollapsed !== this.state.sidebarCollapsed) {
      this.props.onSidebarCollapsed?.(this.state.sidebarCollapsed);
    }
  }

  protected disableCollapseSidebar(): boolean {
    return false;
  }

  render(): ReactElement {
    // let border = `1px solid rgba(128, 128, 128, 0.2)`;
    let border = null;
    let content = this.state.selectedSidebarItem?.render?.(this.props.path) || this.props.renderer?.renderPageWithSidebarContainerContent();
    if (!content) {
      content = <NotFoundFragment path={this.props.path}/>;
    }
    const sidebarLocation = this.props.sidebarLocation;
    const sm = true;//this.theme.breakpoints.up("sm");
    return <Box style={{
      display: "flex",
      flexDirection: sm ? "row" : "column",
      flexGrow: 1,
      overflowY: "hidden", ...this.props.style
    }}>
      {!this.props.sidebarHidden && (!sidebarLocation || sidebarLocation === SidebarLocation.LEFT)
        ? this.renderSidebar()
        : null}
      <Box display="flex" flexGrow={1}>
        <Box display='flex'
             flexDirection="column"
             style={{
               borderLeft: border,
               borderRight: border,
               marginLeft: 'auto',
               marginRight: 'auto',
               flexGrow: 1,
               zIndex: 1,
               position: "relative",
               backgroundColor: this.theme.palette.background.default,
             }}>
          {content}
        </Box>
      </Box>
      {!this.props.sidebarHidden && (sidebarLocation === SidebarLocation.RIGHT)
        ? this.renderSidebar()
        : null
      }
    </Box>;
  }

  private renderSidebar() {
    const sidebarLocation = this.props.sidebarLocation;
    const sidebarCollapsed = Boolean(this.state.sidebarCollapsed);
    const hasActionPanel = Boolean(this.props.sidebarAction);
    const sidebarLeft = (!sidebarLocation || sidebarLocation === SidebarLocation.LEFT);
    const sidebarRight = sidebarLocation === SidebarLocation.RIGHT;
    return <Box style={{
      display: "flex",
      flexDirection: "column",
      width: sidebarCollapsed ? PD_SM * 2 + 40 : SIDEBAR_SIZES.get(this.props.size) || SIDEBAR_WIDTH,
      flexShrink: 0,
      borderRight: sidebarLeft ? DIVIDER : null,
      borderLeft: sidebarRight ? DIVIDER : null,
      background: this.theme.palette.background.default,
      overflowY: "scroll",
    }}>
      {!sidebarCollapsed
        ? (this.props.sidebarTabs || this.props.sidebarItems
          ? <Sidebar
            path={this.props.path}
            title={this.props.title}
            styleFlags={this.props.sidebarStyleFlags}
            background={this.props.sidebarBackground}
            tabs={this.props.sidebarTabs || [{
              id: "default",
              title: this.props.sidebarItems?.title,
              items: this.props.sidebarItems,
              menuOptions: this.props.menuOptions,
              action: this.props.sidebarAction,
              selected: this.state.selectedSidebarItem,
              listener: this,
            }]}
          />
          : this.props.renderer?.renderPageWithSidebarContainerSidebar())
        : null}
      {this.props.sidebarCollapsible
        ? <Button onClick={() => this.setState({sidebarCollapsed: !sidebarCollapsed})}
                  style={{
                    zIndex: 100,
                    background: gray,
                    color: "black",
                    minWidth: 0,
                    padding: PD_SM,
                    position: 'absolute',
                    left: sidebarLeft?PD_SM:null,
                    right: sidebarLeft ? null: PD_SM,
                    bottom: PD_MD + (hasActionPanel ? SZ_ACTION_PANEL : 0),
                  }}>
          {sidebarCollapsed
            ? sidebarLeft ? <KeyboardDoubleArrowRightOutlined/> : <KeyboardDoubleArrowLeftOutlined/>
            : sidebarLeft ? <KeyboardDoubleArrowLeftOutlined/> : <KeyboardDoubleArrowRightOutlined/>}
        </Button>
        : null}
    </Box>;
  }

  onMenuButtonClicked(menuOption: MenuOption): boolean {
    return this.props.onMenuButtonClicked?.(menuOption);
  }

  onSelectionChanged(item: SidebarItem) {
    this.setState({
      selectedSidebarItem: item,
    });
  }
}

export class DialogWithSidebarContainer extends PageWithSidebarContainer {

  constructor(props: PageWithSidebarContainerProps, context: any) {
    super(props, context);
    props.sidebarItems?.groups?.flatMap(group => group.items).map(item => item.onClick = () => {
      this.setState({
        selectedSidebarItem: item,
      });
      return true;
    });
  }

  protected disableCollapseSidebar(): boolean {
    return true;
  }
}
